import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, finalize, Observable, take } from 'rxjs';
import { BaseService } from 'src/app/core/services/base.httpService';
import { handleErrorMessage } from 'src/app/shared/operators/error.operator';
import { LocalStorageService } from 'src/app/shared/services/local-storage.service';
import { DataTypes, STORAGE_KEYS, StatusCodes } from 'src/app/shared/utility/constants';
import { resolveProfileActiveLocationId } from 'src/app/shared/utility/user-profile-all-facilities.util';
import { URLConstants } from 'src/app/shared/utility/url-constants';
import { UserProfileInfo } from '../components/user-profile/user-profile.model';
import { UserEffectivePermissionsService } from './user-effective-permissions.service';

@Injectable({
  providedIn: 'root',
})
export class UserService {
  public requiredUserInfoData = new BehaviorSubject<any>({});
  public isUserPrevSummary = new BehaviorSubject<any>('');
  public isFromUserPageSummary = new BehaviorSubject<string>('');
  public isDeletedDepartmentsData = new BehaviorSubject<string>('');
  public userLoginHistory = new BehaviorSubject<any>('');
  public userProfileInfo = new BehaviorSubject<any>('');

  private isProfileLoaded = false;

  private readonly baseService = inject(BaseService);
  private readonly localStorageService = inject(LocalStorageService);
  private readonly userEffectivePermissionsService = inject(
    UserEffectivePermissionsService
  );
  private readonly errorHandler = handleErrorMessage();

  public setRequiredDataForUserInfoPage(data: any): void {
    this.requiredUserInfoData.next(data);
  }

  public isUserSummaryPrevSearch(data: string) {
    this.isUserPrevSummary.next(data);
  }

  public setUserPageFrom(data: string) {
    this.isFromUserPageSummary.next(data);
  }

  public setDeletedDepartmentsData(data: string) {
    this.isDeletedDepartmentsData.next(data);
  }

  public createUser(data: any): Observable<any> {
    return this.baseService
      .initiateApiRequest('PUT', URLConstants.createUser, data, 'json')
      .pipe(this.errorHandler());
  }

  public updateUser(data: any): Observable<any> {
    return this.baseService
      .initiateApiRequest('POST', URLConstants.updateUser, data, 'json')
      .pipe(this.errorHandler());
  }

  public deleteUser(data: any) {
    return this.baseService
      .initiateApiRequest('POST', URLConstants.deleteUser, data, 'json')
      .pipe(this.errorHandler());
  }

  public fetchUser(
    userId: number | string,
    includeRolesPermissions = false
  ): Observable<any> {
    const query = includeRolesPermissions ? '?includeRolesPermissions=true' : '';
    return this.baseService
      .initiateApiRequest(
        'GET',
        URLConstants.fetchUser + userId + query,
        '',
        'json'
      )
      .pipe(this.errorHandler());
  }

  public getDepartmentRole(): Observable<any> {
    return this.baseService
      .initiateApiRequest('GET', URLConstants.getDepartmentAndRole, '', 'json')
      .pipe(this.errorHandler());
  }

  public userBasicSearch(payload: any): Observable<any> {
    return this.baseService
      .initiateApiRequest(
        'POST',
        URLConstants.basicSearchUser,
        payload,
        '',
        'json'
      )
      .pipe(this.errorHandler());
  }

  public addorUpdateDepartment(data: any) {
    return this.baseService
      .initiateApiRequest('POST', URLConstants.addOrUpdateDepartment, data, 'json')
      .pipe(this.errorHandler());
  }

  public deleteDepartment(data: any) {
    return this.baseService
      .initiateApiRequest('POST', URLConstants.deleteDepartment, data, 'json')
      .pipe(this.errorHandler());
  }

  public fetchUserLoginHistory(userId: number): Observable<any> {
    return this.baseService
      .initiateApiRequest(
        'GET',
        URLConstants.userLoginHistory + userId,
        '',
        'json'
      )
      .pipe(this.errorHandler());
  }

  public sendEmail(data: any): Observable<any> {
    return this.baseService
      .initiateApiRequest('POST', URLConstants.sendEmail, data, 'json')
      .pipe(this.errorHandler());
  }

  public setUserLoginHistory(data: any) {
    return this.userLoginHistory.next(data);
  }

  public fetchUserProfileDetails(): Observable<UserProfileInfo[]> {
    const url = URLConstants.getUserProfileDetails;
    return this.baseService
      .initiateApiRequest('GET', url, '', 'json')
      .pipe(this.errorHandler());
  }

  public updateUserProfileDetails(data: any): Observable<any> {
    const url = URLConstants.updateUserProfileDetails;
    return this.baseService
      .initiateApiRequest('POST', url, data, 'json')
      .pipe(this.errorHandler());
  }

  public setUserProfileDetails(data: UserProfileInfo) {
    return this.userProfileInfo.next(data);
  }

  public applyUserProfileToAppState(
    data: UserProfileInfo,
    options?: { reloadPermissions?: boolean }
  ): void {
    localStorage.setItem(
      STORAGE_KEYS.USER_PROFILE_CACHE,
      JSON.stringify(data)
    );
    localStorage.setItem(
      'userName',
      `${data.firstName}  ${data.lastName}`
    );
    localStorage.setItem('userId', data.userId.toString());
    const tzLabel = data.timeZoneName?.split(' - ')[1];
    if (tzLabel) {
      localStorage.setItem('timezone', tzLabel);
    }
    localStorage.setItem('timezoneId', String(data.timeZoneId ?? 0));
    this.setUserProfileDetails(data);
    const activeLocationId = resolveProfileActiveLocationId(data);
    this.userEffectivePermissionsService.setActiveLocationId(activeLocationId);
    if (options?.reloadPermissions) {
      this.userEffectivePermissionsService.clearPermissions();
      this.userEffectivePermissionsService.setActiveLocationId(activeLocationId);
      this.userEffectivePermissionsService.ensureLoaded(data.userId);
      return;
    }
    this.userEffectivePermissionsService.ensureLoaded(data.userId);
  }

  public refreshCurrentUserProfileIfSelf(
    updatedUserId: number
  ): void {
    const normalizedUpdatedId = Number(updatedUserId);
    const currentUserId = this.getLoggedInUserId();

    if (
      !Number.isFinite(normalizedUpdatedId) ||
      normalizedUpdatedId <= 0 ||
      normalizedUpdatedId !== currentUserId
    ) {
      return;
    }

    this.syncUserProfileFromServer();
  }

  private syncUserProfileFromServer(): void {
    this.fetchUserProfileDetails()
      .pipe(take(1))
      .subscribe({
        next: (response: any) => {
          if (
            response.statusCode === StatusCodes.Success &&
            response.data
          ) {
            this.applyUserProfileToAppState(response.data, {
              reloadPermissions: true,
            });
            return;
          }
          this.userEffectivePermissionsService.abandonPermissionsBootstrap();
        },
        error: () => {
          this.userEffectivePermissionsService.abandonPermissionsBootstrap();
        },
      });
  }

  private getLoggedInUserId(): number {
    const profileValue = this.userProfileInfo.getValue();
    if (
      profileValue &&
      typeof profileValue === DataTypes.Object &&
      profileValue !== null
    ) {
      const profileUserId = Number((profileValue as UserProfileInfo).userId);
      if (Number.isFinite(profileUserId) && profileUserId > 0) {
        return profileUserId;
      }
    }

    const cachedProfile = this.parseStoredUserProfile(
      localStorage.getItem(STORAGE_KEYS.USER_PROFILE_CACHE) ?? ''
    );
    if (cachedProfile?.userId != null) {
      const cachedUserId = Number(cachedProfile.userId);
      if (Number.isFinite(cachedUserId) && cachedUserId > 0) {
        return cachedUserId;
      }
    }

    const storedUserId = Number(localStorage.getItem('userId'));
    if (Number.isFinite(storedUserId) && storedUserId > 0) {
      return storedUserId;
    }

    return NaN;
  }

  public getUserProfileFromStorage(): boolean {
    const profileData = localStorage.getItem(STORAGE_KEYS.USER_PROFILE_CACHE);
    const data = profileData ? this.parseStoredUserProfile(profileData) : null;
    if (data) {
      this.applyUserProfileToAppState(data);
      return true;
    }
    return false;
  }

  public ensureUserProfileLoaded(): void {
    // Optimistic bootstrap from cache (may be stale on Stage until server refresh below).
    this.getUserProfileFromStorage();

    const token = this.localStorageService.getItem(STORAGE_KEYS.JWT_TOKEN);
    if (!token || this.isProfileLoaded) {
      return;
    }

    this.isProfileLoaded = true;
    this.fetchUserProfileDetails()
      .pipe(
        take(1),
        finalize(() => {
          this.isProfileLoaded = false;
        })
      )
      .subscribe({
        next: (response: any) => {
          if (
            response.statusCode === StatusCodes.Success &&
            response.data
          ) {
            this.applyUserProfileToAppState(response.data, {
              reloadPermissions: true,
            });
            return;
          }
          this.userEffectivePermissionsService.abandonPermissionsBootstrap();
        },
        error: () => {
          this.userEffectivePermissionsService.abandonPermissionsBootstrap();
        },
      });
  }

  private hasProfileInMemory(): boolean {
    const profileValue = this.userProfileInfo.getValue();
    return (
      profileValue &&
      typeof profileValue === DataTypes.Object &&
      typeof (profileValue as UserProfileInfo).userId === DataTypes.Number
    );
  }

  private parseStoredUserProfile(raw: string): UserProfileInfo | null {
    try {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === DataTypes.Object && this.isUserProfileShape(parsed)) {
        return parsed;
      }
      return null;
    } catch {
      return null;
    }
  }

  private isUserProfileShape(value: object): value is UserProfileInfo {
    return typeof (value as UserProfileInfo).userId === DataTypes.Number;
  }

  public fetchAlertNotifications(): Observable<any> {
    return this.baseService
      .initiateApiRequest('GET', URLConstants.fetchAlertNotifications, '', 'json')
      .pipe(this.errorHandler());
  }

  public markNotificationsAsRead(ids: number[]): Observable<any> {
    return this.baseService
      .initiateApiRequest(
        'POST',
        URLConstants.updateAlertNotificationStatus,
        { ids },
        'json'
      )
      .pipe(this.errorHandler());
  }
}
